// TypeScript port of backend/api/schemas/bot_flow.py (PKT-0034 Phase 1).
//
// Source of truth lives in the Pydantic models. Keep this file 1:1 with
// the literal unions + step types there — if a step type is added on the
// Python side, mirror it here and the discriminated union below catches
// any drift at compile time.

export type TemplateChannel = "email" | "sms" | "instagram_dm";

export type FlowMode = "L2_draft_only" | "L3_faq_auto" | "L4_full_auto";

export type FlowTriggerType = "inbound_message" | "new_lead" | "scheduled";

export type FlowRunStatus =
  | "active"
  | "escalated"
  | "completed"
  | "timed_out"
  | "failed";

export interface TemplateLookup {
  channel: TemplateChannel;
  template_type: string;
}

export interface BusinessHours {
  start: string; // HH:MM 24-hour
  end: string;
  timezone: string; // IANA tz
}

export interface EscalateTriggers {
  urgent_move_date_days?: number;
  sensitive_keywords?: string[];
  tour_today_keywords?: string[];
  low_confidence_threshold?: number;
  max_user_msgs_per_hour?: number;
  business_hours?: BusinessHours | null;
}

// Step types — discriminated union over `type`.

export interface SendTemplateStep {
  step_id: number;
  type: "send_template";
  template_lookup: TemplateLookup;
  wait_for_reply?: boolean;
  timeout_hours?: number | null;
  context_inject?: Record<string, string> | null;
  on_reply?: Record<string, number> | null;
}

export interface ExtractFieldsStep {
  step_id: number;
  type: "extract_fields";
  fields: string[];
  on_complete: Record<string, number>;
  on_missing_field: Record<string, number>;
}

export interface SaveLeadStep {
  step_id: number;
  type: "save_lead";
  lead_fields: string[];
  source: string;
}

export interface NotifyOperatorStep {
  step_id: number;
  type: "notify_operator";
  via: Array<"workbench_badge" | "email_digest" | "sms_blast">;
}

export interface CompleteStep {
  step_id: number;
  type: "complete";
}

export type FlowStep =
  | SendTemplateStep
  | ExtractFieldsStep
  | SaveLeadStep
  | NotifyOperatorStep
  | CompleteStep;

export interface BotFlow {
  id: string;
  user_id: string;
  name: string;
  description?: string | null;
  channel: TemplateChannel;
  trigger_type: FlowTriggerType;
  trigger_config: Record<string, unknown>;
  steps: FlowStep[];
  escalate_triggers: EscalateTriggers;
  mode: FlowMode;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface BotFlowRun {
  id: string;
  bot_flow_id: string;
  user_id: string;
  client_id?: string | null;
  thread_id: string;
  current_step: number;
  state: Record<string, unknown>;
  status: FlowRunStatus;
  escalated_at?: string | null;
  escalation_reason?: string | null;
  completed_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Test-playground trace returned by POST /api/bot-flows/{id}/test.
// Mirrors backend api.services.bot_flow_runner.RunTrace shape.

export interface FlowStepTrace {
  step_id: number;
  step_type: FlowStep["type"];
  outcome: string;
}

export interface BotFlowRunTrace {
  flow_id: string;
  status: FlowRunStatus;
  current_step: number;
  captured_fields: Record<string, unknown>;
  last_outbound_draft: string | null;
  escalated: boolean;
  escalation_reason: string | null;
  steps_executed: FlowStepTrace[];
  cost_usd: number;
  persisted: boolean;
}

// Patch shape for PATCH /api/bot-flows/{id}.
// Server validates via the Phase-1 Pydantic helpers before writing.

export interface BotFlowPatch {
  name?: string;
  description?: string;
  trigger_config?: Record<string, unknown>;
  steps?: FlowStep[];
  escalate_triggers?: EscalateTriggers;
  mode?: FlowMode;
}

// Human-readable mode labels for the UI's mode toggle.
export const FLOW_MODE_LABELS: Record<FlowMode, string> = {
  L2_draft_only: "L2 · Draft only (operator approves every send)",
  L3_faq_auto: "L3 · FAQ auto, lead flow drafts",
  L4_full_auto: "L4 · Full auto",
};
